import { useCallback, useEffect, useState } from 'react';
import type { Canal, Cliente, Finalidad, PermisosCliente } from '../../../api/ops/clientes';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import type { NombreIcono } from '../../../componentes/iconos';
import { Paginacion } from '../../../componentes/Paginacion';
import { Ventana } from '../../../componentes/Ventana';
import { estadoDeError } from '../../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { VERSION_TEXTOS } from '../alta/datos';
import type { ContextoClientes } from '../lista/contexto';
import { CANALES, DECISION_PERMISO, ESTADO_AVISOS, ESTADO_COMERCIAL, ORIGEN_PERMISO, PERMISOS, diaYHora, telefonoLegible, textoDeError } from '../lista/textos';

/** «+34 6** *** 678» y «a***@gmail.com»: el destino en la ventana de 2.4 (como en Entregas). */
function tapaTelefono(t: string): string {
    const d = t.replace(/\D/g, '');
    return d.length < 9 ? t : `+${d.slice(0, 2)} ${d.charAt(2)}** *** ${d.slice(-3)}`;
}
function tapaEmail(e: string): string {
    const [usuario = '', dominio = ''] = e.split('@');
    return dominio === '' ? e : `${usuario.charAt(0)}***@${dominio}`;
}

/** Canales de la ficha: los que tienen algún permiso registrado y los que tiene como contacto (WhatsApp con teléfono, email). */
function canalesDe(cliente: Cliente, permisos: PermisosCliente | null): Canal[] {
    const telefono = cliente.account?.phone ?? cliente.local.phone ?? null;
    const email = cliente.account?.email ?? cliente.local.email ?? null;
    const de = (permisos?.current ?? []).map((p) => p.channel);
    const todos: Canal[] = ['whatsapp', 'email', 'instagram', 'telegram'];
    return todos.filter((k) => de.includes(k) || (k === 'whatsapp' && telefono !== null) || (k === 'email' && email !== null));
}

function destinoDe(cliente: Cliente, canal: Canal, tapado: boolean): string {
    const telefono = cliente.account?.phone ?? cliente.local.phone ?? '';
    const email = cliente.account?.email ?? cliente.local.email ?? '';
    if (canal === 'whatsapp' || canal === 'sms' || canal === 'voice') {
        return tapado ? tapaTelefono(telefono) : telefonoLegible(telefono);
    }
    if (canal === 'email') {
        return tapado ? tapaEmail(email) : email;
    }
    if (canal === 'instagram' && cliente.local.instagram_username != null) {
        return `@${cliente.local.instagram_username}`;
    }
    return 'su chat';
}

/**
 * Permisos (2.3): el estado por canal (avisos y comercial, con «Pendiente de confirmar» en amarillo, G162) y el registro,
 * que solo crece. «Registrar» abre 2.4. Registrar sigue disponible con el negocio en solo lectura (`x-read-only-mode:
 * allowed`), con el permiso `clientes.consentimientos.registrar` (Recepción también).
 */
export function Permisos({ c, cliente }: { c: ContextoClientes; cliente: Cliente }) {
    const pag = usePaginacionCursor();
    const [datos, setDatos] = useState<PermisosCliente | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [registrar, setRegistrar] = useState<Canal | null>(null);
    const { ops, negocio, permisos } = c;
    const puedeRegistrar = permisos.puede(PERMISOS.consentimientos) && cliente.status !== 'anonymized' && cliente.status !== 'blocked';

    useEffect(() => {
        let vivo = true;
        ops.listCustomerConsents(negocio, cliente.id, { cursor: pag.cursor })
            .then((d) => {
                if (vivo) {
                    setDatos(d);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, cliente.id, pag.cursor, intento]);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    if (error !== null && datos === null) {
        return <EstadoError seccion="los permisos" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={c.movil} />;
    }
    if (datos === null) {
        return null;
    }

    const estado = (canal: Canal, finalidad: Finalidad) => datos.current.find((p) => p.channel === canal && p.purpose === finalidad)?.state ?? 'none';
    const canales = canalesDe(cliente, datos);

    return (
        <div className="rv-cl-seccion">
            <p className="rv-cl-texto">Confirmaciones, pases y recordatorios le llegan siempre. La información comercial, solo si ha dado su permiso y lo ha confirmado.</p>
            <div className="rv-cl-lineas">
                {canales.map((canal) => {
                    const avisos = ESTADO_AVISOS[estado(canal, 'service')];
                    const comercial = ESTADO_COMERCIAL[estado(canal, 'marketing')];
                    return (
                        <div key={canal} className="rv-cl-entrada rv-cl-entrada--canal">
                            <span className="rv-cl-entrada__baldosa">
                                <Icono nombre={CANALES[canal].icono} />
                            </span>
                            <span className="rv-cl-linea">
                                <strong>
                                    {CANALES[canal].nombre}
                                    {cliente.preferred_channel === canal && <em className="rv-cl-preferido">Canal preferido</em>}
                                </strong>
                                <span>{destinoDe(cliente, canal, false)}</span>
                            </span>
                            <span className="rv-cl-estados">
                                <span>
                                    <small>Avisos</small>
                                    <Chip estado={avisos[1]}>{avisos[0]}</Chip>
                                </span>
                                <span>
                                    <small>Comercial</small>
                                    <Chip estado={comercial[1]}>{comercial[0]}</Chip>
                                </span>
                            </span>
                            {puedeRegistrar && (
                                <Boton
                                    tipo="secundario"
                                    icono="pencil"
                                    movil={c.movil}
                                    onClick={() => {
                                        setRegistrar(canal);
                                    }}
                                >
                                    Registrar
                                </Boton>
                            )}
                        </div>
                    );
                })}
            </div>

            <h3 className="rv-cl-subtitulo">Registro de permisos</h3>
            {datos.history.length === 0 ? (
                <p className="rv-cl-vacio">Todavía no hay ningún permiso registrado.</p>
            ) : (
                <div className="rv-cl-lineas">
                    {datos.history.map((r) => {
                        const [texto, chip] = DECISION_PERMISO[r.decision];
                        return (
                            <div key={r.id} className="rv-cl-dato">
                                <span className="rv-cl-linea">
                                    <strong>{`${CANALES[r.channel].nombre} • ${r.purpose === 'marketing' ? 'Información comercial' : 'Confirmaciones y avisos'}`}</strong>
                                    <span>{`${diaYHora(r.occurred_at ?? r.recorded_at)} • ${ORIGEN_PERMISO[r.source]}`}</span>
                                </span>
                                <Chip estado={chip}>{texto}</Chip>
                            </div>
                        );
                    })}
                </div>
            )}
            {(pag.hayAnterior || haySiguiente(datos.next_cursor)) && (
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(datos.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(datos.next_cursor); }} movil={c.movil} />
            )}
            <p className="rv-cl-nota rv-cl-nota--icono">
                <Icono nombre="lock" />
                El registro solo crece: nada se cambia ni se borra. Sirve de prueba ante la autoridad de protección de datos.
            </p>

            {registrar !== null && (
                <VentanaPermiso
                    c={c}
                    cliente={cliente}
                    canal={registrar}
                    onCerrar={() => {
                        setRegistrar(null);
                    }}
                    onHecho={() => {
                        setRegistrar(null);
                        pag.reiniciar();
                        recargar();
                    }}
                />
            )}
        </div>
    );
}

/**
 * 2.4 Registrar un permiso o una baja dados en persona (RV Ventana, N3): para qué (comercial o avisos) y qué ha dicho (da
 * el permiso o se da de baja). El comercial queda «Pendiente de confirmar» hasta que el cliente lo acepte por ese canal
 * (G162, T41); la baja se aplica al momento. Avisa al cliente: sin «Deshacer» (G159).
 */
function VentanaPermiso({ c, cliente, canal, onCerrar, onHecho }: { c: ContextoClientes; cliente: Cliente; canal: Canal; onCerrar: () => void; onHecho: () => void }) {
    const [finalidad, setFinalidad] = useState<Finalidad>('marketing');
    const [da, setDa] = useState(true);
    const [ocupado, setOcupado] = useState(false);
    const nombre = CANALES[canal].nombre;
    const por = canal === 'email' ? 'email' : nombre;
    const [icono, linea]: [NombreIcono, string] = !da
        ? finalidad === 'marketing'
            ? ['ban', `Se aplica en el momento: deja de recibir información comercial por ${por}.`]
            : ['ban', canal === 'email' ? 'Se aplica en el momento. Sus confirmaciones, pases y recordatorios le llegarán por otro canal que tenga permitido.' : `Se aplica en el momento. Sus confirmaciones, pases y recordatorios dejan de llegarle por ${nombre} y le llegan por email.`]
        : finalidad === 'marketing'
          ? ['send', `Queda pendiente de confirmar: le enviamos un mensaje por ${por} para que lo confirme. Hasta entonces no recibe campañas.`]
          : ['check', `Vuelve a recibir confirmaciones, pases y recordatorios por ${por}.`];
    const avisa = da && finalidad === 'marketing';

    const registrar = () => {
        setOcupado(true);
        c.ops
            .recordCustomerConsent(c.negocio, cliente.id, { channel: canal, purpose: finalidad, granted: da, notice_version: VERSION_TEXTOS })
            .then((r) => {
                c.avisar({
                    tipo: 'ok',
                    texto: r.decision === 'pending_confirmation' ? `Pendiente de confirmar: le hemos enviado un mensaje por ${por} para que lo confirme.` : r.decision === 'withdrawn' ? `Baja registrada en ${nombre}. Se aplica ya.` : `Permiso registrado en ${nombre}.`,
                });
                onHecho();
            })
            .catch((e: unknown) => {
                c.avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido registrarlo. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const segmento = <T,>(etiqueta: string, opciones: readonly [T, string][], valor: T, cambiar: (v: T) => void) => (
        <div className="rv-cl-eleccion">
            <span className="rv-cl-eleccion__titulo">{etiqueta}</span>
            <div role="radiogroup" aria-label={etiqueta} className="rv-cl-eleccion__opciones">
                {opciones.map(([v, texto]) => (
                    <button
                        key={texto}
                        type="button"
                        role="radio"
                        aria-checked={v === valor}
                        className="rv-cl-eleccion__b"
                        onClick={() => {
                            cambiar(v);
                        }}
                    >
                        {texto}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <Ventana
            abierta
            titulo="Registrar un permiso o una baja"
            texto={`${nombre} · ${destinoDe(cliente, canal, true)}. Lo que el cliente te ha dicho en persona. Queda registrado a tu nombre.`}
            accion={avisa ? (c.movil ? 'Registrar' : 'Registrar y enviar') : 'Registrar'}
            accionIcono={avisa ? 'send' : 'check'}
            ocupado={ocupado}
            ocupadoTexto="Registrando…"
            movil={c.movil}
            onAccion={registrar}
            onCerrar={onCerrar}
        >
            <div className="rv-cl-ventana-campos">
                {segmento<Finalidad>('Para qué', [['marketing', 'Comercial'], ['service', 'Avisos']], finalidad, setFinalidad)}
                {segmento<boolean>('Qué te ha dicho', [[true, 'Da el permiso'], [false, 'Se da de baja']], da, setDa)}
                <p className="rv-cl-nota rv-cl-nota--icono">
                    <Icono nombre={icono} />
                    {linea}
                </p>
            </div>
        </Ventana>
    );
}
